// Estado compartido de la ayuda del backoffice.
//
// Tres cosas viven acá porque las tres son globales a la aplicacion y ninguna
// pertenece a una pantalla en particular:
//
//   1. El modo señalar. Cambia el cursor a "?" y convierte el proximo clic en
//      una consulta al manual en vez de en una accion.
//   2. El recorrido guiado de la pantalla actual.
//   3. El ofrecimiento del recorrido la primera vez que se entra a una
//      pantalla que tiene uno.
//
// El modo señalar intercepta el clic en fase de CAPTURA y lo detiene ahi. Si
// esperara a la fase de burbujeo, el boton ya habria hecho lo suyo: alguien que
// pregunta "¿que hace este boton?" habria bloqueado la cuenta para averiguarlo.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useLocation } from 'react-router-dom';
import { HelpCircle, X } from 'lucide-react';
import { openDocs } from '@/help/docs';
import { resolveHelp } from '@/help/targets';
import { tourById, tourForPath, type Tour } from '@/help/tours';
import TourOverlay from '@/help/TourOverlay';

const SEEN_TOURS_KEY = 'authsys-admin-tours-seen';

function readSeenTours(): string[] {
  try {
    const raw = window.localStorage.getItem(SEEN_TOURS_KEY);
    return raw ? (JSON.parse(raw) as string[]) : [];
  } catch {
    return [];
  }
}

function rememberTour(id: string): void {
  try {
    const seen = new Set(readSeenTours());
    seen.add(id);
    window.localStorage.setItem(SEEN_TOURS_KEY, JSON.stringify([...seen]));
  } catch {
    // Modo privado o almacenamiento bloqueado: el recorrido se vuelve a
    // ofrecer, que es molesto pero inofensivo.
  }
}

interface HelpApi {
  /** Modo señalar activo. */
  pointing: boolean;
  togglePointing: () => void;
  stopPointing: () => void;
  /** Abre el manual en el punto de un id de ayuda (o en la portada). */
  openHelp: (id?: string) => void;
  /** Arranca un recorrido. Sin id, el de la pantalla actual. */
  startTour: (id?: string) => void;
  /** Recorrido disponible para la pantalla actual, si hay. */
  tourHere: Tour | null;
}

const HelpContext = createContext<HelpApi | null>(null);

export function useHelp(): HelpApi {
  const api = useContext(HelpContext);
  if (!api) throw new Error('useHelp fuera de <HelpProvider>');
  return api;
}

export default function HelpProvider({ children }: { children: ReactNode }): JSX.Element {
  const location = useLocation();
  const [pointing, setPointing] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [tour, setTour] = useState<{ tour: Tour; index: number } | null>(null);
  const [offered, setOffered] = useState<Tour | null>(null);
  const noticeTimer = useRef<number | null>(null);

  const tourHere = useMemo(() => tourForPath(location.pathname), [location.pathname]);

  const flash = useCallback((message: string) => {
    setNotice(message);
    if (noticeTimer.current) window.clearTimeout(noticeTimer.current);
    noticeTimer.current = window.setTimeout(() => setNotice(null), 4000);
  }, []);

  const openHelp = useCallback((id?: string) => {
    const target = id ? resolveHelp(id) : null;
    void openDocs(target?.page, target?.anchor);
  }, []);

  const stopPointing = useCallback(() => setPointing(false), []);
  const togglePointing = useCallback(() => setPointing((v) => !v), []);

  const startTour = useCallback(
    (id?: string) => {
      const next = id ? tourById(id) : tourHere;
      if (!next) {
        flash('Esta pantalla todavía no tiene un recorrido.');
        return;
      }
      setPointing(false);
      setOffered(null);
      rememberTour(next.id);
      setTour({ tour: next, index: 0 });
    },
    [flash, tourHere],
  );

  // --- Modo señalar --------------------------------------------------------

  useEffect(() => {
    if (!pointing) {
      document.documentElement.removeAttribute('data-help-pointer');
      document.querySelectorAll('[data-help-hover]').forEach((el) => {
        el.removeAttribute('data-help-hover');
      });
      return;
    }

    document.documentElement.setAttribute('data-help-pointer', 'on');
    let hovered: Element | null = null;

    function markHovered(el: Element | null): void {
      if (el === hovered) return;
      hovered?.removeAttribute('data-help-hover');
      hovered = el;
      hovered?.setAttribute('data-help-hover', 'on');
    }

    function onMove(e: MouseEvent): void {
      const el = e.target instanceof Element ? e.target.closest('[data-help]') : null;
      markHovered(el);
    }

    function onClick(e: MouseEvent): void {
      // El panel del propio modo señalar no se señala a si mismo.
      if (e.target instanceof Element && e.target.closest('[data-help-ui]')) return;

      e.preventDefault();
      e.stopPropagation();

      const el = e.target instanceof Element ? e.target.closest('[data-help]') : null;
      const id = el?.getAttribute('data-help') ?? null;
      const target = id ? resolveHelp(id) : null;

      setPointing(false);

      if (target) {
        void openDocs(target.page, target.anchor);
        return;
      }
      // Sin explicacion propia, abrir la de la pantalla es mejor que no abrir
      // nada: quien pregunta esta perdido, y la portada de la seccion contesta
      // casi siempre.
      const fallback = document.querySelector('[data-help-page]')?.getAttribute('data-help-page');
      const page = fallback ? resolveHelp(fallback) : null;
      if (page) {
        flash('Ese elemento no tiene una explicación propia: abrimos la de la pantalla.');
        void openDocs(page.page, page.anchor);
      } else {
        flash('Ese elemento no tiene una explicación asociada.');
      }
    }

    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') setPointing(false);
    }

    document.addEventListener('mousemove', onMove, true);
    document.addEventListener('click', onClick, true);
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('mousemove', onMove, true);
      document.removeEventListener('click', onClick, true);
      document.removeEventListener('keydown', onKey, true);
      markHovered(null);
      document.documentElement.removeAttribute('data-help-pointer');
    };
  }, [flash, pointing]);

  // Cambiar de pantalla cancela el modo señalar y cualquier recorrido: los dos
  // apuntan a elementos que ya no existen.
  useEffect(() => {
    setPointing(false);
    setTour(null);
  }, [location.pathname]);

  // --- Ofrecimiento del recorrido -----------------------------------------

  useEffect(() => {
    if (!tourHere) {
      setOffered(null);
      return;
    }
    if (readSeenTours().includes(tourHere.id)) {
      setOffered(null);
      return;
    }
    setOffered(tourHere);
  }, [tourHere]);

  const api = useMemo<HelpApi>(
    () => ({ pointing, togglePointing, stopPointing, openHelp, startTour, tourHere }),
    [openHelp, pointing, startTour, stopPointing, togglePointing, tourHere],
  );

  return (
    <HelpContext.Provider value={api}>
      {children}

      {pointing ? (
        <div
          data-help-ui="banner"
          role="status"
          /* Abajo y flotante, no una barra arriba: una barra arriba tapa
             justamente la barra superior del portal — incluido el botón de
             ayuda con el que se sale del modo. */
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[90] flex items-center gap-3 rounded-full bg-brand text-white pl-4 pr-2 py-2 text-sm shadow-2xl"
        >
          <HelpCircle size={16} aria-hidden="true" />
          <span>
            Hacé clic en cualquier parte de la pantalla para leer qué hace.{' '}
            <kbd className="rounded bg-white/20 px-1">Esc</kbd> para salir.
          </span>
          <button
            type="button"
            onClick={stopPointing}
            className="rounded p-1 hover:bg-white/20"
            aria-label="Salir del modo ayuda"
          >
            <X size={16} aria-hidden="true" />
          </button>
        </div>
      ) : null}

      {offered && !tour ? (
        <div
          data-help-ui="offer"
          className="fixed bottom-4 right-4 z-[80] w-72 rounded-xl border border-surface-border dark:border-surface-dark-border bg-surface dark:bg-surface-dark shadow-xl p-4 space-y-2"
        >
          <p className="text-sm font-semibold">¿Primera vez en esta pantalla?</p>
          <p className="text-sm text-ink-muted">
            Hay un recorrido de {offered.steps.length} pasos que muestra para qué sirve cada
            parte.
          </p>
          <div className="flex items-center gap-2 pt-1">
            <button
              type="button"
              onClick={() => startTour(offered.id)}
              className="min-h-[2.25rem] rounded-lg bg-brand hover:bg-brand-hover text-white text-sm font-medium px-3"
            >
              Ver el recorrido
            </button>
            <button
              type="button"
              onClick={() => {
                rememberTour(offered.id);
                setOffered(null);
              }}
              className="min-h-[2.25rem] rounded-lg px-3 text-sm text-ink-muted hover:text-ink dark:hover:text-ink-inverted"
            >
              Ahora no
            </button>
          </div>
        </div>
      ) : null}

      {notice ? (
        <div
          data-help-ui="notice"
          role="status"
          /* Arriba: abajo al centro vive el aviso del modo señalar, y abajo a
             la derecha el ofrecimiento del recorrido. */
          className="fixed top-4 left-1/2 -translate-x-1/2 z-[95] max-w-md rounded-lg bg-ink text-ink-inverted dark:bg-surface-dark-muted px-4 py-2 text-sm shadow-xl"
        >
          {notice}
        </div>
      ) : null}

      {tour ? (
        <TourOverlay
          tourTitle={tour.tour.title}
          steps={tour.tour.steps}
          index={tour.index}
          onIndexChange={(index) => setTour((t) => (t ? { ...t, index } : t))}
          onClose={() => setTour(null)}
        />
      ) : null}
    </HelpContext.Provider>
  );
}
